import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChalkboardTeacher, Plus, Trash, Key } from "@phosphor-icons/react";

export default function TeachersPage() {
  const [teachers, setTeachers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "Prof@2026", bio: "", phone: "" });
  const [sel, setSel] = useState(() => new Set());

  async function load() { const r = await api.get("/teachers"); setTeachers(r.data); setSel(new Set()); }
  useEffect(() => { load(); }, []);

  async function create() {
    try {
      await api.post("/teachers", form);
      toast.success("Professor cadastrado com sucesso!");
      setOpen(false); setForm({ name: "", email: "", password: "Prof@2026", bio: "", phone: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }

  async function remove(id) {
    if (!confirm("Excluir professor? Os alunos ficarão sem professor atribuído.")) return;
    await api.delete(`/teachers/${id}`); toast.success("Professor removido"); load();
  }

  async function resetPwd(t) {
    if (!confirm(`Redefinir a senha de "${t.name}"?\n\nUma nova senha temporária será enviada por email para ${t.email}. Ela terá que ser trocada no próximo login.`)) return;
    try {
      const r = await api.post(`/users/${t.id}/reset-password`);
      if (r.data.email_sent) toast.success("Senha redefinida — email enviado");
      else if (r.data.temp_password) prompt("Senha temporária (copie manualmente — falha no email):", r.data.temp_password);
      else toast.warning("Senha redefinida, mas o email não pôde ser enviado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }

  function toggleOne(id) {
    setSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleAll() {
    setSel(sel.size === teachers.length && teachers.length > 0 ? new Set() : new Set(teachers.map(t => t.id)));
  }
  async function bulkDel() {
    const ids = [...sel];
    if (!ids.length) return;
    if (!confirm(`Excluir ${ids.length} professor(es)? Os alunos vinculados ficarão sem professor atribuído.`)) return;
    try {
      const r = await api.post(`/teachers/bulk-delete`, { ids });
      toast.success(`${r.data.deleted} excluído(s)${r.data.failed.length ? ` • ${r.data.failed.length} falhou` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-6xl space-y-6 sm:space-y-8">
      <header className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="pill-badge bg-white text-[#1E5FFF] border border-[#1E5FFF]/20 mb-4">
            <ChalkboardTeacher size={12} weight="fill" /> Corpo docente
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-[#12143A] leading-tight">Professores</h1>
          <p className="text-zinc-500 text-sm sm:text-base mt-2">Cadastre professores e vincule alunos a cada um deles.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-teacher-btn" className="btn-pill h-12 gradient-primary hover:opacity-90 text-white shadow-[0_8px_24px_rgba(30,95,255,0.35)] hover:-translate-y-0.5">
              <Plus size={16} className="mr-2" weight="bold" /> Novo professor
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-black">Cadastrar professor</DialogTitle>
              <DialogDescription>Cadastre um professor. Ele receberá a senha temporária por email para o primeiro acesso.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Nome</Label><Input data-testid="t-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
              <div><Label>Email</Label><Input data-testid="t-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
              <div><Label>Senha inicial</Label><Input data-testid="t-pass" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="mt-2 h-12 rounded-2xl"/></div>
              <div><Label>Telefone (WhatsApp) — opcional</Label><Input data-testid="t-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Ex: 55 11 91234-5678" className="mt-2 h-12 rounded-2xl"/></div>
              <div><Label>Bio / especialidade</Label><Textarea value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} className="mt-2 rounded-2xl" rows={2}/></div>
              <Button data-testid="t-submit" onClick={create} className="w-full h-12 btn-pill gradient-primary hover:opacity-90 text-white">Cadastrar professor</Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>

      <div className="card-soft overflow-hidden fade-up">
        <div className="p-4 sm:p-6 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-heading text-lg sm:text-xl font-black">Professores cadastrados</h3>
          <div className="flex items-center gap-3 flex-wrap">
            {sel.size > 0 && (
              <Button data-testid="bulk-del-teachers" variant="outline" onClick={bulkDel}
                      className="h-10 rounded-full border-[#FF3D71]/40 text-[#FF3D71] hover:bg-[#FFE9EE] hover:text-[#FF3D71]">
                <Trash size={14} weight="bold" className="mr-1" /> Excluir {sel.size} selecionado(s)
              </Button>
            )}
            <span className="text-sm text-zinc-500 font-mono-num">{teachers.length} total</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100 bg-zinc-50/50">
                <th className="pl-6 py-4 w-10">
                  <input data-testid="select-all-teachers" type="checkbox"
                         checked={sel.size === teachers.length && teachers.length > 0}
                         onChange={toggleAll}
                         className="w-4 h-4 rounded accent-[#1E5FFF] cursor-pointer" />
                </th>
                <th className="py-4 px-6 font-bold">Professor</th>
                <th className="font-bold">Email</th>
                <th className="font-bold">Bio</th>
                <th className="pr-6 text-right font-bold">Ação</th>
              </tr>
            </thead>
            <tbody>
              {teachers.map(t => (
                <tr key={t.id} className={`border-b border-zinc-50 transition-colors ${sel.has(t.id) ? "bg-[#E0EBFF]/40" : "hover:bg-zinc-50/50"}`}>
                  <td className="pl-6">
                    <input data-testid={`sel-teacher-${t.id}`} type="checkbox"
                           checked={sel.has(t.id)}
                           onChange={() => toggleOne(t.id)}
                           className="w-4 h-4 rounded accent-[#1E5FFF] cursor-pointer" />
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-heading font-black text-xs">{(t.name || "??").split(" ").slice(0,2).map(x=>x[0]).join("").toUpperCase()}</span>
                      </div>
                      <div className="font-bold text-[#12143A]">{t.name}</div>
                    </div>
                  </td>
                  <td className="text-zinc-500">{t.email}</td>
                  <td className="text-zinc-500 text-xs max-w-xs truncate">{t.bio || "—"}</td>
                  <td className="pr-6 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`reset-pw-teacher-${t.id}`} variant="ghost" size="sm" onClick={() => resetPwd(t)}
                              title="Redefinir senha (envia por email)"
                              className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#F79009] hover:bg-[#FFF6EA]">
                        <Key size={14} weight="fill" />
                      </Button>
                      <Button data-testid={`remove-teacher-${t.id}`} variant="ghost" size="sm" onClick={() => remove(t.id)}>
                        <Trash size={14} className="text-[#FF3D71]" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {teachers.length === 0 && (
                <tr><td colSpan={5} className="text-center py-16 text-zinc-400">
                  <div className="text-4xl mb-2">👩‍🏫</div>Nenhum professor cadastrado ainda.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
